import { Component, OnDestroy, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ClipboardService } from 'ngx-clipboard';

import { untilDestroyed, UrlParamsService } from '@app/core';
import { User, WebhookFormatEnum, FailedWebhook, WebhookEndpoint } from '@app/models';
import { AppService, EnterpriseService, ErrorHandlerService, NotyService, UserService } from '@app/services';
import { WebhooksService } from '@app/services/webhooks.service';
import { CreateEndpointModalService } from '@app/pages/configurations/configurations-webhooks/create-endpoint-modal/create-endpoint-modal.service';
// import { JsonPipe } from '@angular/common';
import { UrlencodePipe } from '@app/shared/pipes/urlencode.pipe';

type LegacyEndpoint = {
  url: string;
  format: WebhookFormatEnum;
};

type LogsByDate = {
  [date: string]: FailedWebhook[];
};

@Component({
  selector: 'app-configurations-webhooks',
  templateUrl: './configurations-webhooks.component.html',
  styleUrls: ['../configurations.scss', './configurations-webhooks.component.scss'],
  animations: [
    trigger('logAnimation', [
      transition(':leave', [
        animate(
          '0.2s ease-out',
          style({
            transform: 'translateX(5px)',
            opacity: 0
          })
        )
      ])
    ])
  ],
  standalone: false,
  providers: [UrlencodePipe]
})
export class ConfigurationsWebhooksComponent implements OnInit, OnDestroy {
  currentUser: User;
  webhooksList: WebhookEndpoint[];
  selectedLog: FailedWebhook;
  legacyWebhooks: LegacyEndpoint;
  groupedFailedWebhooks: LogsByDate = {};
  sortedDates: string[] = [];
  isLoading = false;
  isResendingWebhook = false;
  isLoadingErrorLogs = false;
  currentPage = 1;
  pageSize = 60;
  hasMorePages: boolean;
  WebhookFormatEnum = WebhookFormatEnum;

  constructor(
    public appService: AppService,
    public urlParamsService: UrlParamsService,
    public enterpriseService: EnterpriseService,
    private userService: UserService,
    private notyService: NotyService,
    private errorHandlerService: ErrorHandlerService,
    private translateService: TranslateService,
    private webhooksService: WebhooksService,
    private createEndpointModalService: CreateEndpointModalService,
    private clipboardService: ClipboardService,
    private urlencode: UrlencodePipe
  ) {}

  ngOnInit() {
    this.isLoadingErrorLogs = true;

    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({
        next: user => (this.currentUser = user),
        error: error => this.errorHandlerService.handle(error)
      });

    this.userService
      .getCurrentUser({ fetchPolicy: 'cache-first' })
      .pipe(
        filter(user => !!user),
        tap(user => (this.currentUser = user))
      )
      .subscribe({
        next: () => {
          this.loadFailedWebhooks(this.currentPage);
          this.legacyWebhooks = {
            url: this.currentUser.organization.settings.webhook_url,
            format: this.currentUser.organization.settings.webhook_format
          };
        },
        error: error => this.errorHandlerService.handle(error)
      });

    this.webhooksService.webhooksList$.pipe(untilDestroyed(this)).subscribe({
      next: webhooks => {
        this.webhooksList = webhooks;
      }
    });

    this.webhooksService.isLoading$.pipe(untilDestroyed(this)).subscribe({
      next: isLoading => {
        this.isLoading = isLoading;
      }
    });

    this.webhooksService.getOrganizationWebhooksEndpoints().subscribe({
      error: error => this.errorHandlerService.handle(error)
    });
  }

  ngOnDestroy() {}

  editEndpoint(endpoint: WebhookEndpoint) {
    if (this.checkPermissions()) {
      this.createEndpointModalService.open({ isEditingEndpoint: true, endpoint }).subscribe();
    }
  }

  deleteWebhookEndpoint(id: string) {
    if (this.checkPermissions()) {
      this.webhooksService
        .deleteWebhookEndpoint({ id: id })
        .pipe(switchMap(() => this.webhooksService.getOrganizationWebhooksEndpoints()))
        .subscribe({
          next: () => {
            this.notyService.success(this.translateService.instant('webhooks.deletedWithSuccess'), 2000);
          },
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  deleteLegacyEndpoint() {
    if (this.checkPermissions()) {
      this.webhooksService.deleteLegacyEndpoint().subscribe({
        next: () => {
          this.userService.getCurrentUser({ fetchPolicy: 'network-only' });
          this.legacyWebhooks = {
            url: '',
            format: this.currentUser.organization.settings.webhook_format
          };
        }
      });
    }
  }

  resendFailedWebhook(id: string) {
    this.isResendingWebhook = true;
    this.webhooksService
      .resendFailedWebhook({ id: id })
      .pipe(finalize(() => (this.isResendingWebhook = false)))
      .subscribe({
        next: () => {
          this.selectedLog = {};
          this.groupedFailedWebhooks = this.removeLogFromGroup(this.groupedFailedWebhooks, id);
          this.notyService.success(this.translateService.instant('webhooks.hookSendWithSuccess'), 2000);
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  deactivateEndpoint(id: string, active: boolean) {
    if (this.checkPermissions()) {
      this.webhooksService
        .updateWebhookEndpoint({ id: id, active: !active })
        .pipe(switchMap(() => this.webhooksService.getOrganizationWebhooksEndpoints()))
        .subscribe({
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  unblockEndpoint(id: string) {
    if (this.checkPermissions()) {
      this.webhooksService
        .unblockEndpointMutation({ id: id })
        .pipe(switchMap(() => this.webhooksService.getOrganizationWebhooksEndpoints()))
        .subscribe({
          error: error => this.errorHandlerService.handle(error)
        });
    }
  }

  removeLogFromGroup(groupedLogs: LogsByDate, logId: string): LogsByDate {
    for (const date of Object.keys(groupedLogs)) {
      groupedLogs[date] = groupedLogs[date].filter(log => log.id !== logId);

      if (groupedLogs[date].length === 0) {
        delete groupedLogs[date];
        this.sortedDates = this.sortedDates.filter(d => d !== date);
      }
    }
    return groupedLogs;
  }

  loadFailedWebhooks(page = 1) {
    this.isLoadingErrorLogs = true;
    this.webhooksService
      .getFailedWebhooks(page, this.pageSize)
      .pipe(
        finalize(() => {
          ((this.isLoadingErrorLogs = false), (this.isLoading = false));
        }),
        tap(res => {
          const result = res.data.failedWebhooks;
          this.hasMorePages = result.has_more_pages;

          if (!this.selectedLog && result.data.length > 0) {
            this.selectedLog = result.data[0];
          }

          this.groupByDateAndAppend(result.data);
        })
      )
      .subscribe({
        error: error => this.errorHandlerService.handle(error)
      });
  }

  groupByDateAndAppend(webhooks: FailedWebhook[]) {
    for (const webhook of webhooks) {
      const dateObj = new Date(webhook.failed_at);
      const date = dateObj.toISOString().split('T')[0];

      if (!this.groupedFailedWebhooks[date]) {
        this.groupedFailedWebhooks[date] = [];
        this.sortedDates.push(date);
      }

      this.groupedFailedWebhooks[date].push(webhook);
    }
  }

  getFriendlyWebhookFormatName(type: WebhookFormatEnum): string {
    switch (type) {
      case WebhookFormatEnum.Json:
        return 'JSON';
      default:
        return 'URL encoded';
    }
  }

  loadMoreFailedWebhookPages() {
    if (this.hasMorePages && !this.isLoadingErrorLogs) {
      this.currentPage += 1;
      this.loadFailedWebhooks(this.currentPage);
    }
  }

  openCreateEndpointModal() {
    if (this.checkPermissions()) {
      this.createEndpointModalService
        .open({})
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.webhooksService.isLoadingSubject.next(false);
          })
        )
        .subscribe();
    }
  }

  copyToClipboard(response?: string) {
    const payload = this.selectedLog?.payload;
    const format = this.selectedLog?.payload?.format;
    const isJson = String(format ?? '').toLowerCase() === WebhookFormatEnum.Json.toLowerCase();

    let textToCopy = '';

    if (response) {
      textToCopy = response;
    } else if (isJson) {
      textToCopy = typeof payload === 'string' ? payload : JSON.stringify(payload ?? {}, null, 2);
    } else {
      const toEncode = payload != null && typeof payload === 'object' ? payload : { value: payload ?? '' };
      textToCopy = this.urlencode.transform(toEncode) ?? '';
    }

    this.clipboardService.copyFromContent(textToCopy);
    this.notyService.success(this.translateService.instant('shared.textCopySuccess'), 2000);
  }

  checkPermissions(): boolean {
    if (!this.currentUser.currentPermissions.actions_webhooks_oz) {
      this.notyService.error(this.translateService.instant('error_without_permission'), 2000);
      return false;
    }
    return true;
  }
}
